import React, { useState } from 'react';
import { Pressable, StyleProp, StyleSheet, View, ViewStyle } from 'react-native';
import Icon from '../ui/Icon';
import Text from '../ui/Text';
import { colors, radii, spacing } from '../../theme';
import { endVoiceCall } from '../../services/voice';
import type { VoiceEligibility, VoiceSubjectType } from '../../services/voice';
import { useVoiceCall, useVoiceEligibility } from '../../voice/VoiceCallProvider';
import { eligibilityCopy, isLivePhase } from '../../voice/types';

/**
 * "Call in app", wherever there is somebody to call.
 *
 * ---------------------------------------------------------------------------
 * WHY IT ASKS BEFORE IT IS PRESSED
 * ---------------------------------------------------------------------------
 * Eligibility is fetched when the button renders, not when it is tapped. An
 * affordance that fails on press is worse than one that was never offered -
 * and every reason a call is refused is something the customer can already see
 * on the same screen: nobody has been assigned yet, the job has finished, they
 * are on another call. So the button states its own reason next to itself and
 * nobody has to press a control to find out it does not work.
 *
 * ---------------------------------------------------------------------------
 * IT NEVER DISAPPEARS, IT ONLY GOES DARK
 * ---------------------------------------------------------------------------
 * Every refusal keeps the button on screen, disabled, naming the person it
 * would have reached, with the reason underneath. That includes the case where
 * voice is not configured for the deployment at all.
 *
 * Hiding it was the earlier behaviour and it was wrong. A control that is
 * present on one booking and absent on the next teaches a customer that the
 * app is unreliable, when the truth - "nobody is assigned yet" - is something
 * they would have accepted immediately if anyone had said it. Eligibility can
 * come back `canCall: false` with a perfectly good `counterpart`, and that
 * pairing is the whole point: there IS somebody there, they just cannot be
 * rung this second.
 *
 * There is no toast when a call fails. This app has decided against them twice
 * over - feedback belongs on the control that was pressed, or in the call
 * overlay, which is exactly where a failed call reports itself.
 */

type Props = {
  subjectType: VoiceSubjectType;
  subjectId: string;
  /**
   * `filled` - the primary action on a card. Forest fill, white label.
   * `outline` - beside another filled control, so the two do not compete.
   */
  variant?: 'filled' | 'outline';
  /**
   * Overrides the label.
   *
   * Left off, the button names the counterpart once eligibility knows who they
   * are - "Call Ramesh" rather than "Call in app". That is worth more than a
   * generic verb on a screen where the whole question is who you are about to
   * be put through to, and it keeps the disabled state meaningful: a dark
   * button that still names somebody reads as "not right now", where a dark
   * generic one reads as broken.
   */
  label?: string;
  /**
   * Eligibility the parent has already fetched.
   *
   * For a surface that needs the counterpart's *name* as well as permission -
   * `DriverCard` does - so the pair are one request rather than two racing
   * ones that can disagree.
   */
  eligibility?: VoiceEligibility | null;
  /** Still loading the parent's eligibility. Draws the button inert. */
  loading?: boolean;
  /**
   * Called after this device has cleared its own stuck call - see
   * `YOU_ARE_ON_A_CALL` below. Only meaningful when `eligibility` is supplied
   * by the parent: it is the parent's cue to refetch. When this component
   * owns its own eligibility request, it reloads that request itself and
   * this is never called.
   */
  onStaleCallCleared?: () => void;
  style?: StyleProp<ViewStyle>;
};

function CallButton({
  subjectType,
  subjectId,
  variant = 'filled',
  label,
  eligibility: supplied,
  loading: suppliedLoading,
  onStaleCallCleared,
  style,
}: Props) {
  const voice = useVoiceCall();
  const [clearingStaleCall, setClearingStaleCall] = useState(false);

  /*
   * Only asked when the parent has not taken it on.
   *
   * Keyed on whether the prop was *passed*, not on whether it has resolved: a
   * parent that is still loading passes `null`, and treating that as "nobody
   * has asked" would fire a second request alongside the one already in
   * flight. The hook is still called unconditionally - hooks always are - and
   * switched off through `subjectId`, which is what `useAsync`'s `enabled`
   * option exists for.
   */
  const parentOwns = supplied !== undefined;
  const own = useVoiceEligibility(subjectType, parentOwns ? null : subjectId);

  const eligibility = parentOwns ? supplied : own.data;
  const loading = parentOwns ? !!suppliedLoading : own.loading;

  /* A call already up is a reason this one cannot start, and the overlay is
     already on screen saying so - the button just goes inert underneath it. */
  const busy = isLivePhase(voice.phase);
  const allowed = !!eligibility?.canCall && !busy;
  const inert = loading || !allowed;
  const reason = busy ? null : eligibilityCopy(eligibility?.reason ?? null);

  /*
   * `YOU_ARE_ON_A_CALL` can outlive the call it names - a kill mid-call
   * leaves this device with no local record of it, and the server's own
   * cleanup can take hours (see `voice-reaper.service.ts`). When the
   * eligibility read carries a `callId` for it, offer to end that call
   * directly rather than leaving the button dark until it self-heals.
   */
  const staleCallId =
    !busy && eligibility?.reason === 'YOU_ARE_ON_A_CALL'
      ? eligibility.callId
      : null;

  const clearStaleCall = async () => {
    if (!staleCallId || clearingStaleCall) return;
    setClearingStaleCall(true);
    try {
      await endVoiceCall(staleCallId);
    } catch {
      // `/end` is idempotent and this call has almost certainly already
      // settled one way or another - nothing useful to show for a failure
      // here, same reasoning as everywhere else this endpoint is called.
    }
    setClearingStaleCall(false);
    if (parentOwns) {
      onStaleCallCleared?.();
    } else {
      own.reload();
    }
  };

  const filled = variant === 'filled';
  /* Disabled swaps the ink as well as the fill (see `inert` below): white on
     the pale disabled fill would be unreadable, which is the whole complaint
     the opacity version produced. */
  const tint = inert
    ? colors.inkDisabled
    : filled
    ? colors.onPrimary
    : colors.primary;

  const who = eligibility?.counterpart?.displayName?.trim() || null;
  /* First word only. Display names arrive as "Ramesh K." and the full string
     wraps a 44pt control on a small phone; the surname adds nothing here,
     because the card this button sits on is already showing it in full. */
  const given = who ? who.split(/\s+/)[0] : null;
  const shown = label ?? (given ? `Call ${given}` : 'Call in app');

  return (
    <View style={style}>
      <Pressable
        accessibilityRole="button"
        accessibilityLabel={who ? `Call ${who} in the app` : shown}
        accessibilityState={{ disabled: inert }}
        accessibilityHint={
          inert ? reason ?? undefined : 'Places a call over the internet.'
        }
        disabled={inert}
        /* The name travels with the press. If the server comes back saying it
           could never ring them, the failure card has to name who - and by
           then there is no call object to read a name off. */
        onPress={() => voice.startCall(subjectType, subjectId, who)}
        style={({ pressed }) => [
          styles.base,
          filled ? styles.filled : styles.outline,
          pressed ? styles.pressed : null,
          inert ? styles.inert : null,
        ]}
      >
        <Icon
          name={filled ? 'phoneFilled' : 'phone'}
          size={16}
          color={tint}
        />
        <Text variant="labelSm" color={tint} numberOfLines={1}>
          {shown}
        </Text>
      </Pressable>

      {/* The reason, on the control rather than behind a press. Only when it
          is actually refused - a working button needs no caption. */}
      {inert && !loading && reason ? (
        <Text variant="caption" color={colors.inkMuted} style={styles.reason}>
          {reason}
        </Text>
      ) : null}

      {/* Only when the reason names a call that can actually be reached and
          ended - see `staleCallId`. Absent for an ordinary "on another
          call", which is a real conversation nobody should be able to end
          from here. */}
      {staleCallId ? (
        <Pressable
          accessibilityRole="button"
          accessibilityLabel="That call did not end properly. End it."
          disabled={clearingStaleCall}
          onPress={clearStaleCall}
          hitSlop={8}
          style={styles.clearStale}
        >
          <Text
            variant="caption"
            color={colors.primary}
            style={clearingStaleCall ? styles.clearStaleBusy : undefined}
          >
            {clearingStaleCall ? 'Ending…' : "That call didn't end - end it"}
          </Text>
        </Pressable>
      ) : null}
    </View>
  );
}

const styles = StyleSheet.create({
  base: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: spacing.sm,
    paddingVertical: spacing.md,
    paddingHorizontal: spacing.lg,
    borderRadius: radii.sm,
  },
  filled: {
    backgroundColor: colors.primary,
  },
  outline: {
    borderWidth: 1,
    borderColor: colors.primary,
  },
  pressed: {
    opacity: 0.6,
  },
  /* A real fill, not `opacity: 0.45` over a forest-green button - faded to
     that, the label sat near 2:1 and the reported symptom was a Call control
     that could not be read at all. */
  inert: {
    backgroundColor: colors.surfaceMuted,
    borderColor: colors.border,
  },
  reason: {
    marginTop: spacing.xs,
    textAlign: 'center',
  },
  clearStale: {
    marginTop: spacing.xs,
    alignItems: 'center',
  },
  clearStaleBusy: {
    opacity: 0.6,
  },
});

export default CallButton;
